import { useState, type FormEvent } from 'react';

const FORM_ID = 'datic3j2asjjtt1sketg';
const SUBMIT_ADDR = 'https://readdy.ai/api/form/datic3j2asjjtt1sketg';

type Status = { kind: 'ok' | 'err'; text: string } | null;

export default function RsvpSection() {
  const [status, setStatus] = useState<Status>(null);
  const [sending, setSending] = useState(false);

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;

    // Read the live form values at submit time (browser autofill must be included).
    const fd = new FormData(form);
    const honeypot = String(fd.get('website_alt') ?? '').trim();

    // Honeypot hit: pretend it worked, send nothing.
    if (honeypot) {
      setStatus({ kind: 'ok', text: '참석 여부가 전달되었습니다. 감사합니다!' });
      form.reset();
      return;
    }

    const body = new URLSearchParams();
    for (const [key, value] of fd.entries()) {
      if (key === 'website_alt') continue;
      if (typeof value === 'string') body.append(key, value);
    }

    setSending(true);
    setStatus(null);

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body,
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        data?: unknown;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText) as typeof parsed;
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message ||
        parsed?.message ||
        parsed?.meta?.detail ||
        responseText ||
        '';
      const isSpam = String(serverMsg).toLowerCase().includes('spam') || String(serverMsg).includes('form data is spam');
      const ok = response.ok && parsed?.code === 'OK' && !isSpam;

      if (ok) {
        setStatus({ kind: 'ok', text: '참석 여부가 전달되었습니다. 감사합니다!' });
        form.reset();
      } else {
        setStatus({
          kind: 'err',
          text: isSpam
            ? '전송이 차단되었습니다. 잠시 후 다시 시도해 주세요.'
            : `전송에 실패했습니다. ${serverMsg ? `(${serverMsg}) ` : ''}잠시 후 다시 시도해 주세요.`,
        });
      }
    } catch (err) {
      setStatus({
        kind: 'err',
        text: `전송 중 문제가 발생했습니다. ${err instanceof Error ? err.message : ''} 네트워크를 확인하고 다시 시도해 주세요.`,
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="section" id="rsvp">
      <div className="section-head">
        <p className="stamp" data-reveal>
          08 — 참석 여부
        </p>
        <h2 data-reveal>
          함께해 주시는 마음이
          <br />
          <em className="accent">가장 큰</em> 선물입니다
        </h2>
        <p className="sub" data-reveal>
          원활한 예식을 위해 참석 여부를 미리 알려 주세요. 5월 10일까지 전달받습니다.
        </p>
      </div>

      <form
        className="form-wrap"
        id="rsvp-form"
        data-readdy-form
        onSubmit={onSubmit}
        data-form-id={FORM_ID}
      >
        <div className="form-grid-2">
          <div className="field">
            <label htmlFor="rsvp-name">이름</label>
            <input id="rsvp-name" name="name" type="text" placeholder="성함을 입력해 주세요" required />
          </div>
          <div className="field">
            <label htmlFor="rsvp-phone">연락처</label>
            <input id="rsvp-phone" name="phone" type="tel" placeholder="010-0000-0000" />
          </div>
        </div>

        <div className="field">
          <label>참석 여부</label>
          <div className="radio-row">
            <label className="radio-pill">
              <input type="radio" name="attendance" value="참석" required />
              <span>
                <i className="ri-check-line" aria-hidden="true" />
                참석합니다
              </span>
            </label>
            <label className="radio-pill">
              <input type="radio" name="attendance" value="불참" />
              <span>
                <i className="ri-close-line" aria-hidden="true" />
                불참합니다
              </span>
            </label>
          </div>
        </div>

        <div className="form-grid-2">
          <div className="field">
            <label htmlFor="rsvp-guests">참석 인원</label>
            <select id="rsvp-guests" name="guests" defaultValue="1명">
              <option value="1명">1명</option>
              <option value="2명">2명</option>
              <option value="3명">3명</option>
              <option value="4명">4명</option>
              <option value="5명 이상">5명 이상</option>
            </select>
          </div>
          <div className="field">
            <label htmlFor="rsvp-meal">식사 여부</label>
            <select id="rsvp-meal" name="meal" defaultValue="식사 예약">
              <option value="식사 예약">식사 예약함</option>
              <option value="식사 예약 안 함">식사 예약하지 않음</option>
              <option value="미정">아직 미정</option>
            </select>
          </div>
        </div>

        <div className="field">
          <label>구분</label>
          <div className="radio-row">
            <label className="radio-pill">
              <input type="radio" name="side" value="신랑 측" defaultChecked />
              <span>신랑 측 하객</span>
            </label>
            <label className="radio-pill">
              <input type="radio" name="side" value="신부 측" />
              <span>신부 측 하객</span>
            </label>
          </div>
        </div>

        <div className="field">
          <label htmlFor="rsvp-gift">축의금 전달 방식</label>
          <select id="rsvp-gift" name="gift" defaultValue="직접 전달">
            <option value="직접 전달">예식장에서 직접 전달</option>
            <option value="계좌 이체">계좌로 이체</option>
            <option value="미정">아직 미정</option>
          </select>
        </div>

        <div className="field">
          <label htmlFor="rsvp-message">신랑 · 신부에게 전하는 말</label>
          <textarea
            id="rsvp-message"
            name="message"
            maxLength={500}
            placeholder="따뜻한 한마디를 남겨 주세요 (최대 500자)"
          />
        </div>

        {/* anti-spam honeypot */}
        <div className="field-hp" aria-hidden="true">
          <label htmlFor="rsvp-website-alt">Website</label>
          <input
            id="rsvp-website-alt"
            name="website_alt"
            type="text"
            tabIndex={-1}
            autoComplete="off"
            readOnly
          />
        </div>

        {status ? <p className={`form-status ${status.kind}`}>{status.text}</p> : null}

        <button type="submit" className="cta" disabled={sending}>
          {sending ? '전송 중…' : '참석 여부 전하기'}
          <i className="ri-send-plane-line" aria-hidden="true" />
        </button>
      </form>
    </section>
  );
}